import React, { createContext, useContext, useState, useMemo } from 'react';
import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import CustomCircularProgress from '@ui-components/Progress/CustomCircularProgress';

interface LoaderContextType {
  showLoader: () => void;
  hideLoader: () => void;
}

const LoaderContext = createContext<LoaderContextType | undefined>(undefined);

export const useLoader = (): LoaderContextType => {
  const context = useContext(LoaderContext);
  if (!context) {
    throw new Error('useLoader must be used within a LoaderProvider');
  }
  return context;
};

export const LoaderProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [loading, setLoading] = useState<boolean>(false);

  const showLoader = () => setLoading(true);
  const hideLoader = () => setLoading(false);

  const value = useMemo(() => ({ showLoader, hideLoader }), []);

  return (
    <LoaderContext.Provider value={value}>
      {children}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
          }}
        >
          <CustomCircularProgress
            id="global-loader"
            variant="indeterminate"
            color="primary"
            size={60}
          />
        </Box>
      )}
    </LoaderContext.Provider>
  );
};
